import * as React from "react";
import { DataTable } from "@/components/ui/datatable";
import { correctiveActionsColumnConfig } from "@/config/tableColumnConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoreHorizontal, Plus, Loader2, Search, Settings2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Audit, CorrectiveAction, Finding } from "@/app/pages/auditcentre";
import { CorrectiveActionDialog } from "./CorrectiveActionDialog";
import { useTenancy } from "@/contexts/TenancyContext";
import { renderDate } from "@shared/renderDate";
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

interface StandaloneCorrectiveActionsTableProps {
  correctiveActions: CorrectiveAction[];
  audits: Audit[];
  isLoading?: boolean;
  onLink: (actionId: string, findingId: string) => Promise<void>;
  onUnlink: (actionId: string) => Promise<void>;
  // DED-1448: Add support for linking/unlinking to audits
  onLinkToAudit?: (actionId: string, auditId: string) => Promise<void>;
  onUnlinkFromAudit?: (actionId: string) => Promise<void>;
  onCreate: (action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>, findingId?: string) => Promise<void>;
  onUpdate: (id: string, action: Partial<CorrectiveAction>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  findings: Finding[];
  deepLinkedActionId?: string;
}

export function StandaloneCorrectiveActionsTable({
  correctiveActions,
  audits,
  isLoading = false,
  onLink,
  onUnlink,
  onLinkToAudit,
  onUnlinkFromAudit,
  onCreate,
  onUpdate,
  onDelete,
  findings,
  deepLinkedActionId
}: StandaloneCorrectiveActionsTableProps) {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [actionToDelete, setActionToDelete] = React.useState<string | null>(null);
  const { userIdToName } = useTenancy();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [editingAction, setEditingAction] = React.useState<CorrectiveAction | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'Open' | 'Closed'>('all');
  const [showFilters, setShowFilters] = React.useState(false);

  React.useEffect(() => {
    if (!deepLinkedActionId) return;
    const found = correctiveActions.find(a => a.id === deepLinkedActionId);
    if (found) {
      setEditingAction(found);
    }
  }, [deepLinkedActionId, correctiveActions]);

  // Filter corrective actions based on search and filter criteria
  const filteredActions = React.useMemo(() => {
    return correctiveActions.filter(action => {
      const matchesSearch = searchTerm
        ? (action.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           action.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (userIdToName[action.owner] || action.owner)?.toLowerCase().includes(searchTerm.toLowerCase()))
        : true;
      
      const matchesStatus = statusFilter === 'all' ? true : action.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [correctiveActions, searchTerm, statusFilter, userIdToName]);

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border'
      : 'border border-border bg-transparent text-foreground';

  const columns = [
    {
      accessorKey: "summary",
      header: "Summary",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => (
        <Badge variant={row.original.status === "Open" ? "destructiveSubtle" : "successSubtle"}>{row.original.status}</Badge>
      ),
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ row }) => <span>{userIdToName[row.original.owner] === "" || userIdToName[row.original.owner] === undefined ? row.original.owner : userIdToName[row.original.owner]}</span>,
    },
    {
      accessorKey: "dueDate",
      header: "Due Date",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const dueDate = row.original.dueDate;
        if (!dueDate) return <span className="text-muted-foreground">No due date</span>;
        
        const formatted = renderDate(dueDate);
        if (!formatted || formatted === 'Invalid date') {
          return <span className="text-muted-foreground">Invalid date</span>;
        }
        
        return <span>{formatted}</span>;
      },
    },
    {
      accessorKey: "linkedTo",
      header: "Linked Finding",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original as any;
        if (action.findingId && action.findingId !== "none") {
          const finding = findings.find(f => f.id === action.findingId);
          if (finding) {
            return <span title={finding.summary}>{finding.summary.substring(0, 30)}{finding.summary.length > 30 ? '...' : ''}</span>;
          }
          // DED-1892: If findingId exists but finding is not found (deleted), show None
          return <span className="text-muted-foreground">None</span>;
        }
        return <span className="text-muted-foreground">None</span>;
      },
    },
    // DED-1448: Add column to show linked audit
    {
      accessorKey: "linkedAudit",
      header: "Linked Audit",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original as any;
        if (action.auditId) {
          const audit = audits.find(a => a.id === action.auditId);
          if (audit) {
            return <span title={audit.scope}>{audit.scope?.substring(0, 30)}{(audit.scope?.length || 0) > 30 ? '...' : ''}</span>;
          }
          return "Linked to audit";
        }
        return <span className="text-muted-foreground">None</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original;
        return (
          <div id={TOUR_STEP_IDS.CORRECTIVE_ACTIONS_ACTION_BUTTONS}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setEditingAction(action)
              }}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setActionToDelete(action.id);
                  setShowDeleteDialog(true);
                }}
                className="text-red-600"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Delete Corrective Action</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this corrective action? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (actionToDelete) {
                  onDelete(actionToDelete);
                }
                setShowDeleteDialog(false);
                setActionToDelete(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="space-y-4">
        {/* Add Button */}
        <div className="flex justify-end">
          <Button id={TOUR_STEP_IDS.CREATE_CORRECTIVE_ACTION_BUTTON} onClick={() => {
              setEditingAction(null);
              setIsCreateDialogOpen(true);
            }} variant="default" className="mb-3"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Corrective Action
          </Button>
        </div>

        {/* Search Bar with Inline Sliding Filters */}
        <div className="mb-4 relative">
          <div className="relative flex items-center gap-2 border shadow-xs rounded-lg bg-background overflow-hidden transition-all duration-300">
            {/* Search Icon */}
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
              <Search size={16} />
            </div>
            
            {/* Search Input */}
            <Input
              className="flex-1 border-0 pl-10 pr-12 py-2.5 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none!"
              placeholder="Search corrective actions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            
            {/* Filter Pills - Slide from right */}
            <div 
              className={`flex items-center gap-2 pr-12 py-2 transition-all duration-300 ease-in-out ${
                showFilters 
                  ? 'opacity-100 translate-x-0 max-w-full' 
                  : 'opacity-0 translate-x-full max-w-0 overflow-hidden'
              }`}
            >
              <Button
                variant="outline"
                size="sm"
                className={getFilterButtonClass(statusFilter === 'all') + ' h-7 px-3 rounded-full text-xs whitespace-nowrap shadow-sm'}
                onClick={() => setStatusFilter('all')}
              >
                All
              </Button>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
                <SelectTrigger className={getFilterButtonClass(statusFilter !== 'all') + ' h-7 min-w-[90px] rounded-full text-xs border-0 shadow-sm'}>
                  <span className="text-xs">
                    {statusFilter !== 'all' ? statusFilter : 'Status'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Settings Button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 z-10"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Settings2 size={16} className={showFilters ? 'text-primary' : ''} />
            </Button>
          </div>
        </div>

        <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading corrective actions...</span>
            </div>
          </div>
        )}
        <DataTable
          columns={columns}
          data={filteredActions}
          pageSize={10}
          columnDisplayConfig={correctiveActionsColumnConfig}
          sortingId={TOUR_STEP_IDS.CORRECTIVE_ACTIONS_SORTING_BUTTONS}
          onRowClick={(row) => {
            setEditingAction(row);
          }}
        />
      </div>

        <CorrectiveActionDialog
          open={isCreateDialogOpen || !!editingAction}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreateDialogOpen(false);
              setEditingAction(null);
            }
          }}
          action={editingAction}
          onSave={async (data) => {
            console.log('[StandaloneCorrectiveActionsTable] onSave called', { editingAction, data });
            try {
              if (editingAction) {
                console.log('[StandaloneCorrectiveActionsTable] Updating action:', editingAction.id);
                await onUpdate(editingAction.id, data);
                console.log('[StandaloneCorrectiveActionsTable] Update completed');
              } else {
                console.log('[StandaloneCorrectiveActionsTable] Creating new action');
                await onCreate(data);
                console.log('[StandaloneCorrectiveActionsTable] Create completed');
              }
            } catch (error) {
              console.error('[StandaloneCorrectiveActionsTable] Save error:', error);
              throw error;
            }
            setIsCreateDialogOpen(false);
            setEditingAction(null);
          }}
          findings={findings}
          audits={audits}
        />
      </div>
    </>
  );
}
